import { usePermissions } from '@contexts/permissions-provider';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import React from 'react';

import type { PermissionGuardProps } from './PermissionGuard.types';

import { checkPermissions } from './PermissionGuard.utils';

/**
 * Route guard that ensures user has the required permission(s) before rendering children.
 * Shows a loader while permissions are being fetched.
 * Renders a forbidden feedback page inline (inside layout) if user lacks the permission(s).
 *
 * Supports single or multiple permissions with AND/OR logic:
 * - Single: `permission="Users_View_Org"`
 * - Multiple AND (default): `permission={['Perm_A', 'Perm_B']}` — user needs ALL
 * - Multiple OR: `permission={['Perm_A', 'Perm_B']} operator="some"` — user needs ANY
 *
 * @example
 * // Single permission
 * <PermissionGuard permission="reservation_edit">
 *   <EditReservationForm />
 * </PermissionGuard>
 *
 * @example
 * // Multiple permissions — OR (user needs at least one)
 * <PermissionGuard
 *   permission={['AccessPassWithTraining_View_Own', 'AccessPassNoTraining_View_Own']}
 *   operator="some"
 * >
 *   <AccessPassPage />
 * </PermissionGuard>
 *
 * @example
 * // Multiple permissions — AND (user needs all, default)
 * <PermissionGuard permission={['Users_View_Org', 'Users_Edit_Org']}>
 *   <UserManagement />
 * </PermissionGuard>
 */
const PermissionGuard = React.memo(
  ({
    permission,
    operator = 'every',
    children,
  }: Readonly<PermissionGuardProps>): React.ReactElement => {
    const { hasPermission, isLoading } = usePermissions();

    if (isLoading) {
      return <AppLoader />;
    }

    const allowed = checkPermissions(permission, hasPermission, operator);
    if (!allowed) {
      return <InlineFeedback type="forbidden" />;
    }

    return <>{children}</>;
  }
);

PermissionGuard.displayName = 'PermissionGuard';

export { PermissionGuard };
